import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { PieChart, Sparkles, TrendingUp, ExternalLink, Coins as Coin, LineChart, Building2, Shield } from "lucide-react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";

/**
 * PortfolioAllocator — user enters total amount, we show the 20:20:20:20 split
 * (normalised to 25% each so it sums to 100%) across Gold/Silver, Equity,
 * Property, and Insurance/LIC/Term. Pulls one recommended listing per bucket
 * from the "investment" category.
 */
const BUCKETS = [
  {
    key: "metal",
    label: "Gold / Silver",
    weight: 25,
    icon: Coin,
    color: "#DDA77B",
    tagMatchers: ["gold", "silver", "sgb", "copper", "physical"],
    reason: "Hedge against inflation & currency risk.",
  },
  {
    key: "equity",
    label: "Equity",
    weight: 25,
    icon: LineChart,
    color: "#2D5A27",
    tagMatchers: ["equity", "mutual-fund", "mf", "sip", "stocks", "nifty", "index", "elss"],
    reason: "Long-term wealth creation (13-18% CAGR historically).",
  },
  {
    key: "property",
    label: "Property",
    weight: 25,
    icon: Building2,
    color: "#8B5E34",
    tagMatchers: ["land", "plot", "reit", "real-estate", "building"],
    reason: "Physical asset + rental income + appreciation.",
  },
  {
    key: "insurance",
    label: "Insurance / LIC / Term",
    weight: 25,
    icon: Shield,
    color: "#E05E36",
    tagMatchers: ["insurance", "life", "health", "term", "lic", "ulip"],
    reason: "Family safety net + tax deduction (80C/80D).",
  },
];

export default function PortfolioAllocator() {
  const [amount, setAmount] = useState(100000);
  const [byBucket, setByBucket] = useState({});

  useEffect(() => {
    // Load investment listings once and bucket them.
    api.get("/listings", { params: { category: "investment", limit: 60 } })
      .then(r => {
        const bucketed = {};
        BUCKETS.forEach(b => { bucketed[b.key] = []; });
        (r.data || []).forEach(l => {
          const hay = ((l.tags || []).join(" ") + " " + (l.title || "") + " " + (l.description || "")).toLowerCase();
          for (const b of BUCKETS) {
            if (b.tagMatchers.some(t => hay.includes(t))) {
              bucketed[b.key].push(l);
              break;
            }
          }
        });
        setByBucket(bucketed);
      })
      .catch(() => setByBucket({}));
  }, []);

  const perBucket = useMemo(() => amount / 4, [amount]);

  const expectedReturn = useMemo(() => {
    // Weighted average of top pick per bucket's expected_return_pct (fallback ratios if data missing).
    const fallback = { metal: 12, equity: 15, property: 10, insurance: 6 };
    let sum = 0;
    BUCKETS.forEach(b => {
      const pick = byBucket[b.key]?.[0];
      const r = pick?.extra?.expected_return_pct ?? fallback[b.key];
      sum += (b.weight / 100) * (r || 0);
    });
    return sum;
  }, [byBucket]);

  return (
    <div className="rounded-2xl border border-stone-200 bg-gradient-to-br from-white via-white to-emerald-50/40 overflow-hidden" data-testid="portfolio-allocator">
      <div className="px-5 py-4 border-b border-stone-100 flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-[#2D5A27]/10 text-[#2D5A27] grid place-items-center">
          <PieChart size={18} />
        </div>
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-widest font-bold text-stone-500">Recommended split</div>
          <div className="heading text-lg font-extrabold text-stone-900">The 20:20:20:20 rule</div>
          <div className="text-xs text-stone-600 mt-0.5">Balance metals, equity, property & insurance — 25% each — for a resilient long-term portfolio.</div>
        </div>
      </div>

      <div className="px-5 pt-4 pb-2 flex flex-wrap items-end gap-4">
        <div className="flex-1 min-w-[220px]">
          <label className="text-[11px] uppercase tracking-widest font-bold text-stone-500">Total investment amount</label>
          <div className="mt-1 flex items-center gap-2 bg-stone-50 border border-stone-200 rounded-xl px-3 py-2">
            <span className="text-stone-600 font-bold">₹</span>
            <input
              type="number"
              value={amount}
              min={4000}
              step={5000}
              onChange={e => setAmount(Math.max(0, Number(e.target.value) || 0))}
              className="bg-transparent flex-1 outline-none font-bold"
              data-testid="portfolio-amount"
            />
          </div>
        </div>
        <div className="flex-1 min-w-[180px] rounded-xl bg-emerald-50 border border-emerald-200 p-3 flex items-center gap-2">
          <TrendingUp size={16} className="text-emerald-700" />
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-emerald-700">Expected return (weighted)</div>
            <div className="heading text-lg font-extrabold text-emerald-900" data-testid="portfolio-expected-return">
              {expectedReturn.toFixed(1)}% p.a.
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 p-5">
        {BUCKETS.map(b => {
          const Icon = b.icon;
          const items = byBucket[b.key] || [];
          const pick = items[0];
          return (
            <div key={b.key} className="rounded-xl border border-stone-200 p-4 bg-white flex flex-col" data-testid={`bucket-${b.key}`}>
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded-lg grid place-items-center text-white shrink-0" style={{ backgroundColor: b.color }}>
                  <Icon size={14} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[11px] uppercase tracking-widest font-bold text-stone-500">{b.weight}%</div>
                  <div className="font-bold text-stone-900 text-sm truncate">{b.label}</div>
                </div>
                <div className="heading font-extrabold text-[#E05E36] text-lg">₹{perBucket.toLocaleString("en-IN", { maximumFractionDigits: 0 })}</div>
              </div>
              <p className="text-[11px] text-stone-500 mb-2">{b.reason}</p>
              {pick ? (
                <Link
                  to={`/listing/${pick.listing_id}`}
                  className="mt-auto flex items-center gap-2 rounded-lg border border-stone-200 hover:border-[#E05E36] p-2 transition-colors group"
                  data-testid={`bucket-${b.key}-pick`}
                >
                  <Sparkles size={13} className="text-[#E05E36] shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] uppercase tracking-widest font-bold text-stone-500">Top pick</div>
                    <div className="text-xs font-bold text-stone-900 truncate">{pick.title}</div>
                  </div>
                  <ExternalLink size={11} className="text-stone-400 group-hover:text-[#E05E36]" />
                </Link>
              ) : (
                <div className="mt-auto text-[11px] text-stone-400 italic">No listing yet — post the first one!</div>
              )}
            </div>
          );
        })}
      </div>

      <div className="px-5 pb-4 text-[11px] text-stone-500 text-center">
        Rule of thumb — actual mix depends on age, risk profile, and tax bracket. Consult a SEBI-registered advisor for personalised advice.
      </div>
    </div>
  );
}
